import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  ChartBarIcon,
  DocumentTextIcon,
  TrashIcon,
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  LinkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { marked } from 'marked';
import { API_BASE_URL } from '../config/api';
import { exportAPI } from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

interface SymptomCategory {
  name: string;
  description: string;
  icon: string;
  common_symptoms: string[];
}

interface BodyLocation {
  id: string;
  name: string;
  group: string;
}

interface SymptomLog {
  id: string;
  symptom_name: string;
  category: string;
  severity: number;
  body_location?: string;
  duration_minutes?: number;
  onset_time?: string;
  notes?: string;
  associated_factors?: string[];
  triggers?: string[];
  relieving_factors?: string[];
  logged_at: string;
}

interface ResourceLink {
  title: string;
  url: string;
  source: string;
  description?: string;
}

interface PatternAnalysis {
  summary: string;
  patterns_identified: string[];
  potential_triggers: string[];
  correlations: string[];
  recommendations: string[];
  when_to_see_doctor: string[];
  resource_links: ResourceLink[];
  analysis_period_days: number;
  total_symptoms_analyzed: number;
}

interface AssociatedFactors {
  triggers: string[];
  relieving_factors: string[];
  associated_symptoms: string[];
}

interface CustomFactor {
  id: string;
  factor_type: 'trigger' | 'relieving';
  translations: Record<string, string>;
  text: string;
  created_at: string;
}

type TabType = 'log' | 'history' | 'analysis' | 'report';

export const SymptomTracker: React.FC = () => {
  const { t } = useTranslation();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('log');

  // Form state
  const [symptomName, setSymptomName] = useState('');
  const [category, setCategory] = useState('pain');
  const [severity, setSeverity] = useState(5);
  const [bodyLocation, setBodyLocation] = useState('');
  const [durationMinutes, setDurationMinutes] = useState<number | undefined>();
  const [onsetTime, setOnsetTime] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTriggers, setSelectedTriggers] = useState<string[]>([]);
  const [selectedRelievingFactors, setSelectedRelievingFactors] = useState<string[]>([]);

  // Data state
  const [categories, setCategories] = useState<Record<string, SymptomCategory>>({});
  const [bodyLocations, setBodyLocations] = useState<BodyLocation[]>([]);
  const [associatedFactors, setAssociatedFactors] = useState<AssociatedFactors | null>(null);
  const [symptomHistory, setSymptomHistory] = useState<SymptomLog[]>([]);
  const [patternAnalysis, setPatternAnalysis] = useState<PatternAnalysis | null>(null);
  const [historyDays, setHistoryDays] = useState(30);

  // Custom factors state
  const [customFactors, setCustomFactors] = useState<CustomFactor[]>([]);
  const [newTriggerText, setNewTriggerText] = useState('');
  const [newRelievingText, setNewRelievingText] = useState('');
  const [showAddTrigger, setShowAddTrigger] = useState(false);
  const [showAddRelieving, setShowAddRelieving] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showFactors, setShowFactors] = useState(false);
  const [reportContent, setReportContent] = useState<string | null>(null);
  const [reportTitle, setReportTitle] = useState('Symptom Report');

  // Load data on mount
  useEffect(() => {
    fetchCategories();
    fetchBodyLocations();
    fetchAssociatedFactors();
    fetchCustomFactors();
  }, []);

  // Load history on mount and when tab changes
  useEffect(() => {
    fetchSymptomHistory();
  }, [historyDays]);

  useEffect(() => {
    if (activeTab === 'history' || activeTab === 'analysis') {
      fetchSymptomHistory();
    }
  }, [activeTab]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/symptom-tracker/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchBodyLocations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/symptom-tracker/body-locations`);
      if (response.ok) {
        const data = await response.json();
        setBodyLocations(data.locations);
      }
    } catch (error) {
      console.error('Failed to fetch body locations:', error);
    }
  };

  const fetchAssociatedFactors = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/symptom-tracker/associated-factors`);
      if (response.ok) {
        const data = await response.json();
        setAssociatedFactors(data.factors);
      }
    } catch (error) {
      console.error('Failed to fetch associated factors:', error);
    }
  };

  const fetchCustomFactors = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const language = localStorage.getItem('i18nextLng') || 'en';
      const response = await fetch(
        `${API_BASE_URL}/symptom-tracker/custom-factors?language=${language}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomFactors(data.factors);
      }
    } catch (error) {
      console.error('Failed to fetch custom factors:', error);
    }
  };

  const addCustomFactor = async (factorType: 'trigger' | 'relieving', text: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || !text.trim()) return;

      const language = localStorage.getItem('i18nextLng') || 'en';
      const translations: Record<string, string> = { [language]: text.trim() };

      const response = await fetch(`${API_BASE_URL}/symptom-tracker/custom-factors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          factor_type: factorType,
          translations,
        }),
      });

      if (response.ok) {
        await fetchCustomFactors();
        if (factorType === 'trigger') {
          setNewTriggerText('');
          setShowAddTrigger(false);
        } else {
          setNewRelievingText('');
          setShowAddRelieving(false);
        }
      }
    } catch (error) {
      console.error('Failed to add custom factor:', error);
      setError(t('symptomTracker:errors.addFactorFailed', 'Failed to add custom factor'));
    }
  };

  const deleteCustomFactor = async (factorId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/symptom-tracker/custom-factors/${factorId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchCustomFactors();
      }
    } catch (error) {
      console.error('Failed to delete custom factor:', error);
    }
  };

  // Get combined triggers (system + custom)
  const getAllTriggers = (): string[] => {
    const systemTriggers = associatedFactors?.triggers || [];
    const customTriggers = customFactors
      .filter((f) => f.factor_type === 'trigger')
      .map((f) => f.text);
    return [...systemTriggers, ...customTriggers];
  };

  // Get combined relieving factors (system + custom)
  const getAllRelievingFactors = (): string[] => {
    const systemFactors = associatedFactors?.relieving_factors || [];
    const customRelieving = customFactors
      .filter((f) => f.factor_type === 'relieving')
      .map((f) => f.text);
    return [...systemFactors, ...customRelieving];
  };

  // Check if a factor is custom (for showing delete button)
  const isCustomFactor = (text: string, factorType: 'trigger' | 'relieving'): CustomFactor | undefined => {
    return customFactors.find((f) => f.factor_type === factorType && f.text === text);
  };

  const fetchSymptomHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/symptom-tracker/history?days=${historyDays}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSymptomHistory(data.symptoms);
      }
    } catch (error) {
      console.error('Failed to fetch symptom history:', error);
    }
  };

  const handleLogSymptom = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!symptomName.trim()) {
      setError(t('symptomTracker:errors.symptomRequired'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('symptomTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/symptom-tracker/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          symptom_name: symptomName,
          category,
          severity,
          body_location: bodyLocation || null,
          duration_minutes: durationMinutes || null,
          onset_time: onsetTime ? new Date(onsetTime).toISOString() : null,
          notes: notes || null,
          triggers: selectedTriggers.length > 0 ? selectedTriggers : null,
          relieving_factors: selectedRelievingFactors.length > 0 ? selectedRelievingFactors : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to log symptom');
      }

      setSuccess(t('symptomTracker:success.symptomLogged'));
      resetForm();
      fetchSymptomHistory();
    } catch (error) {
      console.error('Log symptom error:', error);
      setError(t('symptomTracker:errors.logFailed'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSymptomName('');
    setCategory('pain');
    setSeverity(5);
    setBodyLocation('');
    setDurationMinutes(undefined);
    setOnsetTime('');
    setNotes('');
    setSelectedTriggers([]);
    setSelectedRelievingFactors([]);
    setShowFactors(false);
  };

  const handleAnalyzePatterns = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('symptomTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/symptom-tracker/analyze-patterns?days=${historyDays}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to analyze patterns');
      }

      const data = await response.json();
      setPatternAnalysis(data);
    } catch (error) {
      console.error('Analyze patterns error:', error);
      setError(t('symptomTracker:errors.analysisFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('symptomTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - historyDays * 24 * 60 * 60 * 1000).toISOString();

      const response = await fetch(
        `${API_BASE_URL}/symptom-tracker/generate-report?start_date=${startDate}&end_date=${endDate}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const data = await response.json();

      // Create report content
      const content = `SYMPTOM REPORT
Generated: ${new Date(data.generated_at).toLocaleString()}
Period: ${new Date(data.date_range.start).toLocaleDateString()} - ${new Date(data.date_range.end).toLocaleDateString()}

### SYMPTOM SUMMARY
${data.symptom_summary.map((s: any) => `- ${s.symptom}: ${s.occurrences} occurrences, avg severity ${s.average_severity}/10`).join('\n')}

### CATEGORY BREAKDOWN
${Object.entries(data.category_breakdown).map(([cat, count]) => `- ${cat}: ${count}`).join('\n')}

### PATTERN ANALYSIS
${data.pattern_analysis}

### RECOMMENDATIONS
${data.recommendations.map((r: string) => `- ${r}`).join('\n')}

---
This report is for informational purposes only and should not replace professional medical advice.`;

      setReportContent(content);
      setReportTitle(`Symptom Report - ${new Date().toLocaleDateString()}`);
    } catch (error) {
      console.error('Generate report error:', error);
      setError(t('symptomTracker:errors.reportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    if (!reportContent) return;
    await exportDocument(format);
  };

  const printReport = () => {
    if (!reportContent) return;

    const htmlContent = marked(reportContent, { async: false }) as string;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; margin-bottom: 10px; }
            h2 { font-size: 20px; margin-top: 20px; margin-bottom: 10px; }
            h3 { font-size: 16px; margin-top: 16px; margin-bottom: 8px; }
            h4 { font-size: 14px; margin-top: 14px; margin-bottom: 6px; }
            p { margin: 12px 0; }
            ul, ol { margin-left: 24px; }
            li { margin-bottom: 4px; }
            strong { font-weight: 600; }
            em { font-style: italic; }
            code { background: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: monospace; }
            pre { background: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; }
            blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; margin: 16px 0; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>${reportTitle}</h1>
          <div class="content">${htmlContent}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportDocument = async (format: 'word' | 'pdf' | 'text') => {
    if (!reportContent) return;

    try {
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `${reportTitle.toLowerCase().replace(/\s+/g, '-')}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content: reportContent,
        title: reportTitle,
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      // Try to use File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            return;
          }
        }
      }

      // Fallback: Standard download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('symptomTracker:errors.exportFailed', 'Failed to export document. Please try again.'));
    }
  };

  const handleDeleteSymptom = async (symptomId: string) => {
    if (!window.confirm(t('symptomTracker:confirm.deleteSymptom'))) return;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/symptom-tracker/log/${symptomId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSymptomHistory(symptomHistory.filter((s) => s.id !== symptomId));
        setSuccess(t('symptomTracker:success.symptomDeleted'));
      }
    } catch (error) {
      console.error('Delete symptom error:', error);
      setError(t('symptomTracker:errors.deleteFailed'));
    }
  };

  const toggleFactor = (factor: string, type: 'trigger' | 'relieving') => {
    if (type === 'trigger') {
      setSelectedTriggers((prev) =>
        prev.includes(factor) ? prev.filter((f) => f !== factor) : [...prev, factor]
      );
    } else {
      setSelectedRelievingFactors((prev) =>
        prev.includes(factor) ? prev.filter((f) => f !== factor) : [...prev, factor]
      );
    }
  };

  const getSeverityColor = (sev: number) => {
    if (sev <= 3) return 'bg-green-500';
    if (sev <= 5) return 'bg-yellow-500';
    if (sev <= 7) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getSeverityLabel = (sev: number) => {
    if (sev <= 2) return t('symptomTracker:severity.minimal');
    if (sev <= 4) return t('symptomTracker:severity.mild');
    if (sev <= 6) return t('symptomTracker:severity.moderate');
    if (sev <= 8) return t('symptomTracker:severity.severe');
    return t('symptomTracker:severity.extreme');
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <ChartBarIcon className="h-12 w-12 text-purple-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('symptomTracker:title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('symptomTracker:subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('log')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'log'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <PlusIcon className="h-4 w-4 inline mr-1" />
            {t('symptomTracker:tabs.log')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <CalendarIcon className="h-4 w-4 inline mr-1" />
            {t('symptomTracker:tabs.history')}
          </button>
          <button
            onClick={() => setActiveTab('analysis')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'analysis'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <LightBulbIcon className="h-4 w-4 inline mr-1" />
            {t('symptomTracker:tabs.analysis')}
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'report'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <DocumentTextIcon className="h-4 w-4 inline mr-1" />
            {t('symptomTracker:tabs.report')}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-4xl mx-auto w-full mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="max-w-4xl mx-auto w-full mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-green-700 dark:text-green-300 text-sm">{success}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto max-w-4xl mx-auto w-full">
        {/* Log Symptom Tab */}
        {activeTab === 'log' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <form onSubmit={handleLogSymptom} className="space-y-6">
              {/* Symptom Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('symptomTracker:form.symptomName')} *
                </label>
                <input
                  type="text"
                  value={symptomName}
                  onChange={(e) => setSymptomName(e.target.value)}
                  placeholder={t('symptomTracker:form.symptomPlaceholder')}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                  list="common-symptoms"
                />
                <datalist id="common-symptoms">
                  {categories[category]?.common_symptoms.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>

              {/* Category & Severity Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('symptomTracker:form.category')}
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    {Object.entries(categories).map(([key, cat]) => (
                      <option key={key} value={key}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {categories[category]?.description}
                  </p>
                </div>

                {/* Severity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('symptomTracker:form.severity')}: {severity}/10 - {getSeverityLabel(severity)}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={severity}
                    onChange={(e) => setSeverity(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{t('symptomTracker:severity.minimal')}</span>
                    <span>{t('symptomTracker:severity.extreme')}</span>
                  </div>
                  <div className={`mt-2 h-2 rounded ${getSeverityColor(severity)}`} style={{ width: `${severity * 10}%` }} />
                </div>
              </div>

              {/* Body Location & Duration Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Body Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('symptomTracker:form.bodyLocation')}
                  </label>
                  <select
                    value={bodyLocation}
                    onChange={(e) => setBodyLocation(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">{t('symptomTracker:form.selectLocation')}</option>
                    {bodyLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('symptomTracker:form.duration')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      value={durationMinutes || ''}
                      onChange={(e) => setDurationMinutes(e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="0"
                      className="w-24 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-gray-600 dark:text-gray-400">{t('symptomTracker:form.minutes')}</span>
                  </div>
                </div>
              </div>

              {/* Onset Date (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('symptomTracker:form.onsetDate')} <span className="text-gray-400 dark:text-gray-500 font-normal">({t('symptomTracker:form.optional')})</span>
                </label>
                <input
                  type="date"
                  value={onsetTime}
                  onChange={(e) => setOnsetTime(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:[color-scheme:dark]"
                />
              </div>

              {/* Triggers & Relieving Factors */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowFactors(!showFactors)}
                  className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400"
                >
                  {showFactors ? (
                    <ChevronUpIcon className="h-4 w-4 mr-1" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                  )}
                  {t('symptomTracker:form.addFactors')}
                </button>

                {showFactors && associatedFactors && (
                  <div className="mt-4 space-y-4">
                    {/* Triggers */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('symptomTracker:form.triggers')}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {getAllTriggers().map((trigger) => {
                          const customFactor = isCustomFactor(trigger, 'trigger');
                          return (
                            <div key={trigger} className="relative group inline-flex">
                              <button
                                type="button"
                                onClick={() => toggleFactor(trigger, 'trigger')}
                                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                  selectedTriggers.includes(trigger)
                                    ? 'bg-purple-600 text-white'
                                    : customFactor
                                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800 border border-purple-300 dark:border-purple-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {trigger}
                              </button>
                              {customFactor && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteCustomFactor(customFactor.id);
                                  }}
                                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title={t('symptomTracker:form.deleteCustomFactor', 'Delete')}
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                        {/* Add custom trigger button */}
                        {!showAddTrigger ? (
                          <button
                            type="button"
                            onClick={() => setShowAddTrigger(true)}
                            className="px-3 py-1 rounded-full text-sm bg-purple-100 dark:bg-purple-900/50 border border-purple-300 dark:border-purple-600 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors flex items-center gap-1"
                          >
                            <PlusIcon className="h-3 w-3" />
                            {t('symptomTracker:form.addCustom', 'Add')}
                          </button>
                        ) : (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={newTriggerText}
                              onChange={(e) => setNewTriggerText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addCustomFactor('trigger', newTriggerText);
                                } else if (e.key === 'Escape') {
                                  setShowAddTrigger(false);
                                  setNewTriggerText('');
                                }
                              }}
                              placeholder={t('symptomTracker:form.customTriggerPlaceholder', 'New trigger...')}
                              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => addCustomFactor('trigger', newTriggerText)}
                              className="p-1 text-purple-600 hover:text-purple-800 dark:text-purple-400"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddTrigger(false);
                                setNewTriggerText('');
                              }}
                              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Relieving Factors */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('symptomTracker:form.relievingFactors')}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {getAllRelievingFactors().map((factor) => {
                          const customFactor = isCustomFactor(factor, 'relieving');
                          return (
                            <div key={factor} className="relative group inline-flex">
                              <button
                                type="button"
                                onClick={() => toggleFactor(factor, 'relieving')}
                                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                  selectedRelievingFactors.includes(factor)
                                    ? 'bg-green-600 text-white'
                                    : customFactor
                                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800 border border-green-300 dark:border-green-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {factor}
                              </button>
                              {customFactor && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteCustomFactor(customFactor.id);
                                  }}
                                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title={t('symptomTracker:form.deleteCustomFactor', 'Delete')}
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                        {/* Add custom relieving factor button */}
                        {!showAddRelieving ? (
                          <button
                            type="button"
                            onClick={() => setShowAddRelieving(true)}
                            className="px-3 py-1 rounded-full text-sm bg-green-100 dark:bg-green-900/50 border border-green-300 dark:border-green-600 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-800 transition-colors flex items-center gap-1"
                          >
                            <PlusIcon className="h-3 w-3" />
                            {t('symptomTracker:form.addCustom', 'Add')}
                          </button>
                        ) : (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={newRelievingText}
                              onChange={(e) => setNewRelievingText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addCustomFactor('relieving', newRelievingText);
                                } else if (e.key === 'Escape') {
                                  setShowAddRelieving(false);
                                  setNewRelievingText('');
                                }
                              }}
                              placeholder={t('symptomTracker:form.customRelievingPlaceholder', 'What helped...')}
                              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-green-500"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => addCustomFactor('relieving', newRelievingText)}
                              className="p-1 text-green-600 hover:text-green-800 dark:text-green-400"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddRelieving(false);
                                setNewRelievingText('');
                              }}
                              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('symptomTracker:form.notes')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('symptomTracker:form.notesPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !symptomName.trim()}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? t('symptomTracker:form.logging') : t('symptomTracker:form.logSymptom')}
              </button>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Time Period Selector */}
            <div className="flex justify-between items-center mb-4">
              <select
                value={historyDays}
                onChange={(e) => setHistoryDays(parseInt(e.target.value))}
                className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value={7}>{t('symptomTracker:history.last7Days')}</option>
                <option value={14}>{t('symptomTracker:history.last14Days')}</option>
                <option value={30}>{t('symptomTracker:history.last30Days')}</option>
                <option value={90}>{t('symptomTracker:history.last90Days')}</option>
              </select>
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                {symptomHistory.length} {t('symptomTracker:history.symptoms')}
              </span>
            </div>

            {symptomHistory.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                <CalendarIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">{t('symptomTracker:history.noSymptoms')}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">{t('symptomTracker:history.startLogging')}</p>
              </div>
            ) : (
              symptomHistory.map((symptom) => (
                <div
                  key={symptom.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">
                          {symptom.symptom_name}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs text-white ${getSeverityColor(symptom.severity)}`}>
                          {symptom.severity}/10
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs">
                          {categories[symptom.category]?.name || symptom.category}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {new Date(symptom.logged_at).toLocaleString()}
                          </span>
                          {symptom.body_location && (
                            <span>
                              {bodyLocations.find((l) => l.id === symptom.body_location)?.name ||
                                symptom.body_location}
                            </span>
                          )}
                          {symptom.duration_minutes && (
                            <span>
                              {symptom.duration_minutes} {t('symptomTracker:form.minutes')}
                            </span>
                          )}
                        </div>
                        {symptom.onset_time && (
                          <div className="mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {t('symptomTracker:form.onsetTime')}: {new Date(symptom.onset_time).toLocaleString()}
                            </span>
                          </div>
                        )}
                        {symptom.triggers && symptom.triggers.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1 mt-2">
                            <span className="text-xs font-medium text-purple-700 dark:text-purple-300 mr-1">
                              {t('symptomTracker:form.triggers')}:
                            </span>
                            {symptom.triggers.map((trigger: string) => (
                              <span
                                key={trigger}
                                className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs"
                              >
                                {trigger}
                              </span>
                            ))}
                          </div>
                        )}
                        {symptom.relieving_factors && symptom.relieving_factors.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1 mt-2">
                            <span className="text-xs font-medium text-green-700 dark:text-green-300 mr-1">
                              {t('symptomTracker:form.relievingFactors')}:
                            </span>
                            {symptom.relieving_factors.map((factor: string) => (
                              <span
                                key={factor}
                                className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs"
                              >
                                {factor}
                              </span>
                            ))}
                          </div>
                        )}
                        {symptom.notes && (
                          <p className="text-gray-500 dark:text-gray-400 italic mt-2">
                            <span className="font-medium not-italic">{t('symptomTracker:form.notes')}:</span> {symptom.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteSymptom(symptom.id)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Analysis Tab */}
        {activeTab === 'analysis' && (
          <div className="space-y-6">
            {/* Analysis Controls */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('symptomTracker:analysis.title')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('symptomTracker:analysis.description')}
                  </p>
                </div>
                <select
                  value={historyDays}
                  onChange={(e) => setHistoryDays(parseInt(e.target.value))}
                  className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value={7}>{t('symptomTracker:history.last7Days')}</option>
                  <option value={14}>{t('symptomTracker:history.last14Days')}</option>
                  <option value={30}>{t('symptomTracker:history.last30Days')}</option>
                  <option value={90}>{t('symptomTracker:history.last90Days')}</option>
                </select>
              </div>
              <button
                onClick={handleAnalyzePatterns}
                disabled={loading || symptomHistory.length === 0}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? t('symptomTracker:analysis.analyzing') : t('symptomTracker:analysis.analyzePatterns')}
              </button>
            </div>

            {/* Analysis Results */}
            {patternAnalysis && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
                {/* Summary */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('symptomTracker:analysis.summary')}
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">
                    {patternAnalysis.summary}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t('symptomTracker:analysis.analyzed', {
                      count: patternAnalysis.total_symptoms_analyzed,
                      days: patternAnalysis.analysis_period_days,
                    })}
                  </p>
                </div>

                {/* Patterns */}
                {patternAnalysis.patterns_identified.length > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                      {t('symptomTracker:analysis.patternsIdentified')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {patternAnalysis.patterns_identified.map((pattern, i) => (
                        <li key={i} className="text-sm text-blue-700 dark:text-blue-300">
                          {pattern}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Triggers */}
                {patternAnalysis.potential_triggers.length > 0 && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">
                      {t('symptomTracker:analysis.potentialTriggers')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {patternAnalysis.potential_triggers.map((trigger, i) => (
                        <li key={i} className="text-sm text-purple-700 dark:text-purple-300">
                          {trigger}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {patternAnalysis.recommendations.length > 0 && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h4 className="font-medium text-green-800 dark:text-green-200 mb-2 flex items-center">
                      <LightBulbIcon className="h-5 w-5 mr-2" />
                      {t('symptomTracker:analysis.recommendations')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {patternAnalysis.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-green-700 dark:text-green-300">
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* When to See Doctor */}
                {patternAnalysis.when_to_see_doctor.length > 0 && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <h4 className="font-medium text-red-800 dark:text-red-200 mb-2 flex items-center">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                      {t('symptomTracker:analysis.whenToSeeDoctor')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {patternAnalysis.when_to_see_doctor.map((item, i) => (
                        <li key={i} className="text-sm text-red-700 dark:text-red-300">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Resource Links */}
                {patternAnalysis.resource_links.length > 0 && (
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                    <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
                      <LinkIcon className="h-5 w-5 mr-2" />
                      {t('symptomTracker:analysis.additionalReading')}
                    </h4>
                    <ul className="space-y-2">
                      {patternAnalysis.resource_links.map((link, i) => (
                        <li key={i} className="text-sm">
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-700 dark:text-indigo-300 hover:underline font-medium"
                          >
                            {link.title}
                          </a>
                          <span className="text-indigo-600 dark:text-indigo-400 ml-2">
                            - {link.source}
                          </span>
                          {link.description && (
                            <p className="text-indigo-600 dark:text-indigo-400 text-xs mt-0.5">
                              {link.description}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Report Tab */}
        {activeTab === 'report' && (
          <div className="space-y-6">
            {/* Generate Report Controls */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="text-center">
                <DocumentTextIcon className="h-16 w-16 mx-auto text-purple-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {t('symptomTracker:report.title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('symptomTracker:report.description')}
                </p>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('symptomTracker:report.selectPeriod')}
                  </label>
                  <select
                    value={historyDays}
                    onChange={(e) => setHistoryDays(parseInt(e.target.value))}
                    className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={7}>{t('symptomTracker:history.last7Days')}</option>
                    <option value={14}>{t('symptomTracker:history.last14Days')}</option>
                    <option value={30}>{t('symptomTracker:history.last30Days')}</option>
                    <option value={90}>{t('symptomTracker:history.last90Days')}</option>
                  </select>
                </div>

                <button
                  onClick={handleGenerateReport}
                  disabled={loading || symptomHistory.length === 0}
                  className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  {loading ? t('symptomTracker:report.generating') : t('symptomTracker:report.generateReport')}
                </button>

                {symptomHistory.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                    {t('symptomTracker:report.noData')}
                  </p>
                )}
              </div>
            </div>

            {/* Report Preview (shown after generation) */}
            {reportContent && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    {t('symptomTracker:report.preview', 'Report Preview')}
                  </h4>
                  <ExportDropdownMenu
                    onExport={handleExport}
                    onPrint={printReport}
                    showPrint={true}
                  />
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
                    {reportContent}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-4xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('symptomTracker:disclaimer.title')}:</strong> {t('symptomTracker:disclaimer.text')}
        </p>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="text-gray-900 dark:text-gray-100">{t('symptomTracker:loading')}</span>
          </div>
        </div>
      )}

    </div>
  );
};
